import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons/faPlusCircle";
import { resources } from "@constants/hardcoded-strings";
import { WORKFLOWS_REFERENCE_DATA_TYPES, WorkflowsReferenceData } from "@constants/reference-data";
import { useModalContext } from "@context/use-modal-context";
import { LabelWidthTypes } from "@enums/label-width-types";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { ModalTypes } from "@enums/modal-types";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { UserPermissionTypes } from "@enums/user-permission-types";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { setTableSettingsTablePagination } from "@redux/actions/table-settings-actions";
import { getWorkflowConfigurationsReduxRequest } from "@redux-requests/workflows";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { extractRelyingPartiesForPropertyName } from "@utils/permissions-util";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { ButtonWithIcon } from "@containers/common/button-with-icon/button-with-icon";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { CardContainer } from "@containers/common/card-container/card-container";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ToggleFormGroup } from "@containers/common/toggle-form-group/toggle-form-group";
import { ContentPageHeaderDocumentationLink } from "@containers/common/content-page-header-documentation-link/content-page-header-documentation-link";
import { AddVerificationWorkflowModal } from "./add-verification-workflow-modal/add-verification-workflow-modal";
import { WorkflowsTableContainer } from "./workflows-table-container/workflows-table-container";
import styles from "./workflows-container.less";
import { getActiveElements, getActiveReferenceData } from "@utils/data-util";
import { getSessionState, getTableSettingsState } from "@redux/selectors/root-selectors";
import { getWorkflowsDataState } from "@redux/selectors/workflows-selectors";
import { formatMessageTemplates } from "@formatters/message-templates/message-template-formatter";

export const WorkflowsContainer = () => {
    const modalContext = useModalContext();
    const dispatch = useDispatch();

    const {
        isLoadingWorkflowConfigurations,
        workflowConfigurations
    } = useSelector(getWorkflowsDataState);
    const { workflows: workflowsTableState } = useSelector(getTableSettingsState);
    const { session } = useSelector(getSessionState);
    const referenceData = useReferenceDataSelector<WorkflowsReferenceData>(WORKFLOWS_REFERENCE_DATA_TYPES);

    const [showArchivedWorkflows, setShowArchivedWorkflows] = useState(false);
    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);
    const [shouldLoadWorkflowConfigurations, setShouldLoadWorkflowConfigurations] = useState(true);

    useEffect(() => {
        if (!referenceData.isLoading.workflows && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getCommonReferenceDataReduxRequest(dispatch)(
                WORKFLOWS_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.SERVICE_CONFIGURATION,
                CommonReferenceDataPageTypes.WORKFLOWS
            );
        }
    }, [referenceData.isLoading.messagingTemplates, shouldLoadReferenceData]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.axnCompanies &&
            referenceData.axnCountries &&
            referenceData.axnIdentityProviders &&
            referenceData.axnRelyingParties &&
            referenceData.selfServiceVerificationMessageTemplates &&
            referenceData.selfServiceVerificationAttributes &&
            referenceData.axnAttributeTypeFields &&
            referenceData.workflowTemplates &&
            referenceData.workflowRedirectURLs &&
            referenceData.axnAvailablePropertyCategories &&
            referenceData.documentationLinks &&
            referenceData.attributeClassifications &&
            referenceData.axnAttributeLabels &&
            referenceData.axnAttributeTypes &&
            referenceData.rpServiceAttributeProperties &&
            referenceData.rpServiceAttributePropertyMaps &&
            referenceData.axnAttributeTypeFieldCustomizations
        );
    }, [
        referenceData.axnCompanies,
        referenceData.axnCountries,
        referenceData.axnIdentityProviders,
        referenceData.axnRelyingParties,
        referenceData.selfServiceVerificationMessageTemplates,
        referenceData.selfServiceVerificationAttributes,
        referenceData.axnAttributeTypeFields,
        referenceData.workflowTemplates,
        referenceData.workflowRedirectURLs,
        referenceData.axnAvailablePropertyCategories,
        referenceData.documentationLinks,
        referenceData.attributeClassifications,
        referenceData.axnAttributeLabels,
        referenceData.axnAttributeTypes,
        referenceData.rpServiceAttributeProperties,
        referenceData.rpServiceAttributePropertyMaps,
        referenceData.axnAttributeTypeFieldCustomizations
    ]);

    useEffect(() => {
        if (isReferenceDataPresent && !isLoadingWorkflowConfigurations && shouldLoadWorkflowConfigurations) {
            setShouldLoadWorkflowConfigurations(false);
            getWorkflowConfigurationsReduxRequest(dispatch)(
                referenceData.axnCompanies,
                referenceData.axnRelyingParties
            );
        }
    }, [isReferenceDataPresent, isLoadingWorkflowConfigurations, shouldLoadWorkflowConfigurations]);


    const {
        axnCompanies: activeAxnCompanies,
        axnRelyingParties: activeAxnRelyingParties
    } = useMemo(() => {
        if (isReferenceDataPresent) {
            const { axnCompanies, axnRelyingParties } = referenceData;

            return getActiveReferenceData({
                axnCompanies,
                axnRelyingParties
            });
        }

        return {
            axnCompanies: [],
            axnRelyingParties: []
        };
    }, [isReferenceDataPresent, referenceData.axnCompanies, referenceData.axnRelyingParties]);

    const activeWorkflowConfigurations = useMemo(() => {
        if (workflowConfigurations) {
            return getActiveElements(workflowConfigurations);
        }

        return [];

    }, [workflowConfigurations]);

    const renderAddVerificationWorkflowModal = useMemo(() => (onModalClose: () => void) => {
        const workflowTemplates = referenceData.workflowTemplates.filter(wt => wt.status === EntityStatusTypes.ACTIVE);

        const allowedCompanies = activeAxnCompanies.filter(company => company.type === "RP");
        const allowedCompanyIds = allowedCompanies.map(vc => vc.id);
        const allowedRelyingParties = extractRelyingPartiesForPropertyName(
            UserPermissionTypes.CREATE_SERVICES,
            session.user.axnAdminUserRPProperties,
            activeAxnRelyingParties
        ).filter(relyingParties => allowedCompanyIds.includes(relyingParties.companyID));

        return (
            <AddVerificationWorkflowModal
                onModalClose={onModalClose}
                session={session}
                axnAvailablePropertyCategories={referenceData.axnAvailablePropertyCategories}
                allAxnCompanies={referenceData.axnCompanies}
                allAxnRelyingParties={referenceData.axnRelyingParties}
                allowedAxnCompanies={allowedCompanies}
                allowedAxnRelyingParties={allowedRelyingParties}
                workflowRedirectURLs={referenceData.workflowRedirectURLs}
                workflowTemplates={workflowTemplates}
                workflowConfigurations={activeWorkflowConfigurations}
            />
        );
    }, [
        referenceData.axnAvailablePropertyCategories,
        activeAxnCompanies,
        activeAxnRelyingParties,
        referenceData.workflowRedirectURLs,
        referenceData.workflowTemplates,
        activeWorkflowConfigurations,
        session.user.axnAdminUserRPProperties
    ]);

    const isLoading = useMemo(() => {
        return isLoadingWorkflowConfigurations || referenceData.isLoading.workflows;
    }, [referenceData.isLoading.workflows, isLoadingWorkflowConfigurations]);

    const isLoaded = useMemo(() => {
        return !!(
            !isLoading &&
            workflowConfigurations &&
            isReferenceDataPresent &&
            !shouldLoadWorkflowConfigurations
        );
    }, [
        isLoading,
        workflowConfigurations,
        isReferenceDataPresent,
        shouldLoadWorkflowConfigurations
    ]);

    const handleSetShowArchivedWorkflows = () => {
        dispatch(setTableSettingsTablePagination(TableSettingsTableTypes.WORKFLOWS, { pageIndex: 0 }));
        setShowArchivedWorkflows(!showArchivedWorkflows);
    };

    const headerButton = useMemo(() => {
        if (session.userPermissions.canCreateWorkflows && isLoaded) {
            const onClick = () => {
                modalContext.setModalState({
                    modalType: ModalTypes.AddVerificationWorkflow,
                    renderFunction: renderAddVerificationWorkflowModal
                });
            };

            if (session.limits.workflows > workflowConfigurations.length) {
                return (
                    <ButtonWithIcon
                        id="add-verification-workflow-button"
                        title={resources.pages.workflows.headerButton}
                        text={resources.pages.workflows.headerButton}
                        onClick={onClick}
                        icon={faPlusCircle}
                        disabled={isLoading || !isLoaded}
                    />
                );
            } else {
                const renderTooltip = (props: any) => {
                    return (
                        <Tooltip {...props}>
                            {resources.pages.workflows.headerButtonDisabledText}
                        </Tooltip>
                    );
                };

                return (
                    <OverlayTrigger overlay={renderTooltip} placement={"auto"}>
                        <div>
                            <ButtonWithIcon
                                id="add-verification-workflow-button"
                                title={null}
                                text={resources.pages.workflows.headerButton}
                                onClick={() => { }}
                                icon={faPlusCircle}
                                disabled={true}
                            />
                        </div>
                    </OverlayTrigger>
                );
            }
        } else {
            return null;
        }
    }, [
        isLoading,
        isLoaded,
        workflowConfigurations,
        session.userPermissions.canCreateWorkflows,
        session.limits.workflows,
        renderAddVerificationWorkflowModal
    ]);


    const filteredWorkflowConfigurations = useMemo(() => {
        if (workflowConfigurations && showArchivedWorkflows) {
            return workflowConfigurations.filter(config => config.status === EntityStatusTypes.INACTIVE);
        } else if (workflowConfigurations && !showArchivedWorkflows) {
            return workflowConfigurations.filter(config => config.status === EntityStatusTypes.ACTIVE);
        } else {
            return [];
        }
    }, [workflowConfigurations, showArchivedWorkflows]);

    return (
        <CardContainer isPageHeight={true}>
            <ContentPageHeader
                title={resources.pages.workflows.header}
                preHeaderButtonElement={
                    <ContentPageHeaderDocumentationLink
                        documentationLinks={referenceData.documentationLinks}
                        shortName={"workflow-list-page-header"}
                    />
                }
                headerButton={headerButton}
            />
            <ContentPageBody isMaxWidth={true} isPageHeight={true}>
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
                {isLoaded && (
                    <>
                        <div className={styles.toggleWorkflowsSection}>
                            <ToggleFormGroup
                                checked={showArchivedWorkflows}
                                controlId={"show-archived-workflows-toggle"}
                                disabled={false}
                                isRequired={false}
                                labelWidth={LabelWidthTypes.EXTRA_SMALL}
                                label={resources.pages.workflows.filters.showArchivedWorkflows.label}
                                ariaLabel={resources.pages.workflows.filters.showArchivedWorkflows.label}
                                onChange={handleSetShowArchivedWorkflows}
                                value={`${showArchivedWorkflows}`}
                                checkedIcon={false}
                                uncheckedIcon={false}
                            />
                        </div>
                        <WorkflowsTableContainer
                            workflowConfigurations={filteredWorkflowConfigurations}
                            canRestoreDueToLimits={session.limits.workflows > workflowConfigurations.length}
                            isPageHeight={true}
                            axnCompanies={referenceData.axnCompanies}
                            axnCountries={referenceData.axnCountries}
                            axnRelyingParties={referenceData.axnRelyingParties}
                            axnAvailablePropertyCategories={referenceData.axnAvailablePropertyCategories}
                            axnIdentityProviders={referenceData.axnIdentityProviders}
                            messageTemplates={formatMessageTemplates(referenceData.selfServiceVerificationMessageTemplates, referenceData.axnCompanies)}
                            showArchivedWorkflows={showArchivedWorkflows}
                            tableState={workflowsTableState}
                            axnAttributeTypeFields={referenceData.axnAttributeTypeFields}
                            axnAttributeTypeFieldCustomizations={referenceData.axnAttributeTypeFieldCustomizations}
                            selfServiceVerificationAttributes={referenceData.selfServiceVerificationAttributes}
                            axnAdminUserRPProperties={session.user.axnAdminUserRPProperties}
                            attributeClassifications={referenceData.attributeClassifications}
                            attributeLabels={referenceData.axnAttributeLabels}
                            attributeTypes={referenceData.axnAttributeTypes}
                            rpServiceAttributeProperties={referenceData.rpServiceAttributeProperties}
                            rpServiceAttributePropertyMaps={referenceData.rpServiceAttributePropertyMaps}
                        />
                    </>
                )}
            </ContentPageBody>
        </CardContainer>
    );
};